import { ComposedModal, ModalBody, ModalHeader } from '@carbon/react';
import style from './area-data-model-modal.module.scss';

interface AreaDataModelModalProps {
  open: boolean;
  onClose: () => void;
}

// ── Diagram geometry ────────────────────────────────────────────────────────
// The "Structure of one area" flow is a fixed 10-box hierarchy, so box and
// connector coordinates are hardcoded against one container size (no layout
// measurement). Boxes are positioned from COL_X + the per-column Y centres;
// connectors read the SAME numbers so the lines always meet the box edges.
const BOX_W = 176;
const BOX_H = 56;
const COL_X = [24, 300, 576, 852];
const DIAGRAM_W = COL_X[3] + BOX_W + 24; // 1052
const DIAGRAM_H = 450;

// Per-column Y centres, staggered to mirror the mock's layout.
const AREA_Y = 260;
const FORMATION_Y = [190, 390];
const TC_Y = [150, 260, 390];
const INV_Y = [120, 210, 260, 390];

// `default` = part of the reported default case (blue border); `muted` = an
// on-the-shelf alternative (grey); `dark` = the root Area box.
type BoxVariant = 'dark' | 'default' | 'muted';

interface Box {
  key: string;
  eyebrow: string;
  title: string;
  suffix?: string;
  x: number;
  y: number;
  variant: BoxVariant;
}

const BOXES: Box[] = [
  {
    key: 'area',
    eyebrow: 'Level 1',
    title: 'Area',
    x: COL_X[0],
    y: AREA_Y,
    variant: 'dark',
  },
  {
    key: 'f1',
    eyebrow: 'Formation',
    title: 'Formation 1',
    x: COL_X[1],
    y: FORMATION_Y[0],
    variant: 'default',
  },
  {
    key: 'f2',
    eyebrow: 'Formation',
    title: 'Formation 2',
    x: COL_X[1],
    y: FORMATION_Y[1],
    variant: 'muted',
  },
  {
    key: 'tc1',
    eyebrow: 'Type Curve',
    title: 'Type Curve 1',
    suffix: 'Spacing 1',
    x: COL_X[2],
    y: TC_Y[0],
    variant: 'default',
  },
  {
    key: 'tc2',
    eyebrow: 'Type Curve',
    title: 'Type Curve 2',
    suffix: 'Spacing 2',
    x: COL_X[2],
    y: TC_Y[1],
    variant: 'muted',
  },
  {
    key: 'tc3',
    eyebrow: 'Type Curve',
    title: 'Type Curve 3',
    suffix: 'Spacing 3',
    x: COL_X[2],
    y: TC_Y[2],
    variant: 'default',
  },
  {
    key: 'i1',
    eyebrow: 'Inventory',
    title: 'Inventory 1',
    x: COL_X[3],
    y: INV_Y[0],
    variant: 'default',
  },
  {
    key: 'i2',
    eyebrow: 'Inventory',
    title: 'Inventory 2',
    x: COL_X[3],
    y: INV_Y[1],
    variant: 'muted',
  },
  {
    key: 'i3',
    eyebrow: 'Inventory',
    title: 'Inventory 3',
    x: COL_X[3],
    y: INV_Y[2],
    variant: 'muted',
  },
  {
    key: 'i4',
    eyebrow: 'Inventory',
    title: 'Inventory 4',
    x: COL_X[3],
    y: INV_Y[3],
    variant: 'default',
  },
];

const BOX_BY_KEY = new Map(BOXES.map((b) => [b.key, b]));

// Edges: `alt` renders the dashed-grey "alternative child"; the rest are the
// solid-blue "default child" chain (arrowhead at the target).
const EDGES: { from: string; to: string; alt?: boolean }[] = [
  { from: 'area', to: 'f1' },
  { from: 'area', to: 'f2' },
  { from: 'f1', to: 'tc1' },
  { from: 'f1', to: 'tc2', alt: true },
  { from: 'f2', to: 'tc3' },
  { from: 'tc1', to: 'i1' },
  { from: 'tc1', to: 'i2', alt: true },
  { from: 'tc2', to: 'i3' },
  { from: 'tc3', to: 'i4' },
];

const edgePath = (from: Box, to: Box): string => {
  const sx = from.x + BOX_W;
  const sy = from.y;
  const tx = to.x;
  const ty = to.y;
  // Orthogonal routing (like the mock): a straight horizontal line when the two
  // boxes share a row, otherwise horizontal → vertical riser at the midpoint →
  // horizontal into the target.
  if (sy === ty) return `M ${sx} ${sy} L ${tx} ${ty}`;
  const midX = sx + (tx - sx) / 2;
  return `M ${sx} ${sy} L ${midX} ${sy} L ${midX} ${ty} L ${tx} ${ty}`;
};

const boxClass = (variant: BoxVariant): string => {
  if (variant === 'dark') return `${style.node} ${style.nodeDark}`;
  if (variant === 'muted') return `${style.node} ${style.nodeMuted}`;
  return `${style.node} ${style.nodeDefault}`;
};

const COLUMNS = [
  { label: '01 Area', x: COL_X[0] },
  { label: '02 Formations', x: COL_X[1] },
  { label: '03 Type Curves + Spacing', x: COL_X[2] },
  { label: '04 Inventories', x: COL_X[3] },
];

const CARDS = [
  {
    num: '01',
    title: 'Area',
    body: 'The root of the model — a geographic or operational unit. Everything below it is scoped to this area, and one area rolls up to a single reported case.',
  },
  {
    num: '02',
    title: 'Formation',
    body: 'A producing interval within the area. An area can carry several formations; they stack rather than compete, so each contributes its own volumes.',
  },
  {
    num: '03',
    title: 'Type curve + spacing',
    body: 'A production forecast for the formation, paired with the well spacing it assumes. Alternatives model a different development case — tighter spacing, a revised curve.',
  },
  {
    num: '04',
    title: 'Inventory',
    body: 'The set of undrilled locations generated from that type curve and spacing. This is what gets scheduled and valued.',
  },
];

const DEFAULT_CASE_ROWS = [
  { label: 'Area', value: 'Area' },
  {
    label: 'Branch A',
    value: 'Formation 1 · Type Curve 1 (Spacing 1) · Inventory 1',
  },
  {
    label: 'Branch B',
    value: 'Formation 2 · Type Curve 3 (Spacing 3) · Inventory 4',
  },
  {
    label: 'On the shelf',
    value: 'Type Curve 2 (Spacing 2) · Inventory 2 · Inventory 3',
  },
];

const RULES = [
  {
    title: 'One default per parent',
    body: 'Never zero, never two. A parent with a single child defaults to it implicitly.',
  },
  {
    title: 'Spacing travels with the type curve',
    body: 'Spacing is an attribute of the type curve, not a separate level — change spacing and you are on a different type curve.',
  },
  {
    title: 'Inventory is always a leaf',
    body: 'Nothing hangs below an inventory. It is the terminal output of one formation–type curve–spacing path.',
  },
];

// Explains how Type Curve Areas, formations, Type Curves and inventory relate.
// Opened from the "View the Area data model" action in the Add or Remove Type
// Curves modal's info Toggletip. Static content — no data dependencies.
export const AreaDataModelModal = ({
  open,
  onClose,
}: AreaDataModelModalProps) => (
  <ComposedModal
    open={open}
    onClose={onClose}
    size="lg"
    className={style.modal}
    aria-label="Area data model"
  >
    <ModalHeader
      label="Area data model"
      title="How Type Curve Areas, formations, Type Curves and inventory relate."
    />
    <ModalBody aria-label="Area data model">
      <div className={style.body}>
        <div className={style.intro}>
          <span className={style.eyebrow}>Insights · Data model</span>
          <h3 className={style.heading}>
            Area hierarchy: formations, type curves, and inventory
          </h3>
          <p className={style.introBody}>
            An area holds one or more formations. Each formation carries type
            curves, each type curve carries an inventory. At every level exactly
            one child is flagged as the default, and the chain of defaults is
            what the area reports when nothing is overridden.
          </p>
        </div>

        <section className={style.diagramCard}>
          <div className={style.diagramHeader}>
            <span className={style.diagramTitle}>Structure of one area</span>
            <div className={style.legend}>
              <span className={style.legendItem}>
                <span className={style.legendLineDefault} aria-hidden="true" />
                Default child
              </span>
              <span className={style.legendItem}>
                <span className={style.legendLineAlt} aria-hidden="true" />
                Alternative child
              </span>
            </div>
          </div>

          <div className={style.diagramScroll}>
            <div
              className={style.diagram}
              style={{ width: DIAGRAM_W, height: DIAGRAM_H }}
            >
              {COLUMNS.map((col) => (
                <span
                  key={col.label}
                  className={style.columnHeader}
                  style={{ left: col.x }}
                >
                  {col.label}
                </span>
              ))}

              <svg
                className={style.connectors}
                width={DIAGRAM_W}
                height={DIAGRAM_H}
                viewBox={`0 0 ${DIAGRAM_W} ${DIAGRAM_H}`}
                aria-hidden="true"
              >
                <defs>
                  <marker
                    id="adm-arrow"
                    markerWidth="7"
                    markerHeight="7"
                    refX="5.5"
                    refY="3"
                    orient="auto"
                  >
                    <path d="M0,0 L6,3 L0,6 Z" fill="#0f62fe" />
                  </marker>
                </defs>
                {EDGES.map((edge) => {
                  const from = BOX_BY_KEY.get(edge.from);
                  const to = BOX_BY_KEY.get(edge.to);
                  if (!from || !to) return null;
                  return (
                    <path
                      key={`${edge.from}-${edge.to}`}
                      d={edgePath(from, to)}
                      className={edge.alt ? style.edgeAlt : style.edgeDefault}
                      markerEnd={edge.alt ? undefined : 'url(#adm-arrow)'}
                      fill="none"
                    />
                  );
                })}
              </svg>

              {BOXES.map((box) => (
                <div
                  key={box.key}
                  className={boxClass(box.variant)}
                  style={{
                    left: box.x,
                    top: box.y - BOX_H / 2,
                    width: BOX_W,
                    height: BOX_H,
                  }}
                >
                  <span className={style.nodeEyebrow}>{box.eyebrow}</span>
                  <span className={style.nodeTitle}>
                    {box.title}
                    {box.suffix && (
                      <span className={style.nodeSuffix}> {box.suffix}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className={style.cards}>
          {CARDS.map((card) => (
            <div key={card.num} className={style.card}>
              <span className={style.cardNum}>{card.num}</span>
              <span className={style.cardTitle}>{card.title}</span>
              <p className={style.cardBody}>{card.body}</p>
            </div>
          ))}
        </div>

        <section className={style.resolveSection}>
          <div className={style.resolveText}>
            <h4 className={style.sectionTitle}>How defaults resolve</h4>
            <p className={style.paragraph}>
              Each parent marks exactly one child as its default. Reading the
              model means walking the solid line from the area down: Formation 1
              → Type Curve 1 → Inventory 1 for the first branch, Formation 2 →
              Type Curve 3 → Inventory 4 for the second. Formation 2 has only
              one child at each level, so its single child is also its default.
            </p>
            <p className={style.paragraph}>
              Dashed lines are the alternatives that exist but are not selected.
              They are fully built objects — Type Curve 2 has its own spacing
              and its own inventory — they just sit outside the default case
              until someone promotes them.
            </p>
            <div className={style.callout}>
              <p>
                <strong>
                  Switching the default re-scopes everything below it
                </strong>{' '}
                Promoting Type Curve 2 to default swaps Inventory 3 into the
                area case in place of Inventory 1. Nothing is deleted; the
                branch simply stops being the one that reports.
              </p>
            </div>
          </div>

          <div className={style.defaultCase}>
            <span className={style.defaultCaseTitle}>
              The area&apos;s default case
            </span>
            <dl className={style.defaultCaseList}>
              {DEFAULT_CASE_ROWS.map((row) => (
                <div key={row.label} className={style.defaultCaseRow}>
                  <dt className={style.defaultCaseLabel}>{row.label}</dt>
                  <dd className={style.defaultCaseValue}>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className={style.rulesSection}>
          <h4 className={style.sectionTitle}>Rules the diagram encodes</h4>
          <div className={style.rules}>
            {RULES.map((rule) => (
              <div key={rule.title} className={style.rule}>
                <span className={style.ruleTitle}>{rule.title}</span>
                <p className={style.ruleBody}>{rule.body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </ModalBody>
  </ComposedModal>
);

AreaDataModelModal.displayName = 'AreaDataModelModal';
